import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ModalContainer } from 'components/Modal/styles';
import TopUpModal from 'components/Modal/TopUpModal';
import { ModalCloseButton, ModalCloseIcon, MuiModal } from 'components/TemplateManager/ModalStyles';
import { STRINGS } from 'config';
import { useCallback, useEffect, useState } from 'react';
import { deleteOrder, getProducts, getWallet } from 'store/action/wallet';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { hasUnlimitedSub, Pass, Passes } from 'store/reducer/wallet';
import { TopUpLink } from 'styles/elements/Links';
import theme from 'styles/theme';
import request from 'superagent';
import { ChipContainer, StyledTimerIcon, TimerText } from './freeTimerStyles';

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const CLOSE_TOP_UP_CHOICE_MODAL = 'CLOSE_TOP_UP_CHOICE_MODAL';
export const SELECTED_PRODUCT_CHOICE = 'SELECTED_PRODUCT_CHOICE';

export function checkIfHasActivePass(passes: Passes | null) {
  if (passes) {
    const { activePass } = passes;
    if (activePass?.id) {
      return true;
    }
  }
  return false;
}

const {
  REDUCERS: {
    WALLET: { SET_SHOW_DAY_PASS_FLAG },
  },
  SUMMARISER: { HOST, BASE_URL },
  STRIPE,
} = STRINGS;

const stripePromise = loadStripe(STRIPE.PUBLISHABLE_KEY);
interface TimeDisplay {
  bgColor: string;
  text: string;
}

export function getTimeDisplay(
  time: number | null,
  isPaid = false,
  isRecording = false,
  activePass: Pass | null,
  displaySeconds?: number,
): TimeDisplay {
  if (time === null || time === undefined) {
    return { bgColor: '', text: '' };
  }

  if (activePass) {
    return { bgColor: theme.colorStatusInfoBg, text: `${capitalize(activePass.label.toLowerCase())} active` };
  }

  const totalSeconds = Math.floor(time);
  const prefix = isPaid ? '' : 'free ';

  // Use displaySeconds for real-time countdown when recording and < 60 seconds
  const effectiveSeconds =
    isRecording && totalSeconds < 60 && totalSeconds > 0 && displaySeconds !== undefined
      ? displaySeconds
      : totalSeconds;

  // 0 seconds left - error color
  if (effectiveSeconds === 0) {
    return {
      bgColor: theme.colorStatusError,
      text: `0 minutes ${prefix}left today.`,
    };
  }

  // Real-time countdown when recording and < 60 seconds
  if (isRecording && totalSeconds < 60 && totalSeconds > 0) {
    return {
      bgColor: theme.colorStatusImportantBg,
      text: `${effectiveSeconds} seconds ${prefix}left today.`,
    };
  }

  // When less than 10 minutes remain it changes to caution
  if (totalSeconds < 600) {
    const minutes = Math.floor(totalSeconds / 60);
    if (totalSeconds < 120) {
      return {
        bgColor: theme.colorStatusImportantBg,
        text: `${totalSeconds} seconds ${prefix}left today.`,
      };
    }
    return {
      bgColor: theme.colorStatusImportantBg,
      text: `${minutes} minutes ${prefix}left today.`,
    };
  }

  // 30 minutes - default start
  if (totalSeconds === 1800) {
    return {
      bgColor: theme.colorStatusInfoBg,
      text: `30 minutes ${prefix}left today.`,
    };
  }

  // More than 10 minutes - show in minutes
  const minutes = Math.floor(totalSeconds / 60);
  return {
    bgColor: theme.colorStatusInfoBg,
    text: `${minutes} minutes ${prefix}left today.`,
  };
}

// poll order status with backoff, stoppable via AbortSignal
export async function pollOrderStatus({
  orderId,
  onUpdated,
  abortSignal,
  selectedPracticeId,
}: {
  orderId: string;
  onUpdated: (order: string) => void;
  abortSignal?: AbortSignal;
  selectedPracticeId: string;
}) {
  let attempts = 0;
  let interval = 5000;
  const fetchOrder = async () => {
    if (abortSignal?.aborted) throw new Error('aborted');
    if (selectedPracticeId) {
      const res = await request
        .get(`${HOST}/payments/orders/${orderId}?practiceId=${selectedPracticeId}`)
        .withCredentials();
      return res.body;
    }
    return null;
  };

  // helper sleep that handles abort
  const sleep = (ms: number) =>
    new Promise<void>(resolve => {
      const t = setTimeout(resolve, ms);
      if (abortSignal) {
        const handler = () => {
          clearTimeout(t);
          resolve();
        };
        abortSignal.addEventListener('abort', handler, { once: true });
      }
    });

  const run = async () => {
    while (!abortSignal?.aborted) {
      attempts += 1;

      try {
        // eslint-disable-next-line no-await-in-loop
        const order = await fetchOrder();
        if (order?.status && order.status !== 'PENDING') {
          onUpdated(order.status);
          return order;
        }
      } catch {
        // swallow transient errors
      }

      if (attempts >= 999) {
        if (interval === 3000) interval = 15000;
        if (attempts >= 999) break;
      }
      // eslint-disable-next-line no-await-in-loop
      await sleep(interval);
    }
    return null;
  };

  return run();
}

export const FreeTimeLeft = () => {
  const {
    app: { isRecordingConnectionOpen, selectedPractice },
    wallet: { topup, products, passes, subscription },
  } = useAppState();
  const dispatch = useAppDispatch();
  const [displaySeconds, setDisplaySeconds] = useState<number | null>(topup?.freeBalanceSeconds ?? null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [currentOrderId, setOrderId] = useState<string | null>(null);
  const [isCompleteCheckout, setCompletedCheckout] = useState<boolean>(false);
  const [isTopUpChoiceModalOpen, setTopUpChoiceModalOpen] = useState(false);
  const selectedPracticeId = selectedPractice?.id;
  const activePass = passes?.activePass || null;
  const hasActivePass = !!activePass;
  const { bgColor, text } = getTimeDisplay(
    displaySeconds,
    (topup?.paidBalanceSeconds ?? 0) !== 0,
    isRecordingConnectionOpen,
    activePass,
    displaySeconds ?? undefined,
  );

  const handleOpenTopUpOptionsModal = useCallback(() => {
    dispatch({ type: SET_SHOW_DAY_PASS_FLAG, payload: false });
    sessionStorage.setItem(CLOSE_TOP_UP_CHOICE_MODAL, 'false');
    sessionStorage.setItem(SELECTED_PRODUCT_CHOICE, 'NONE');
    if (products === null && selectedPracticeId) {
      getProducts(dispatch, selectedPracticeId);
    }
    setTopUpChoiceModalOpen(true);
  }, [dispatch, products, selectedPracticeId]);

  const handleUpdatePaymentStatus = useCallback(
    (orderStatus: string) => {
      if (orderStatus === 'PAID' && selectedPracticeId) {
        toast.success('Thank you. Your purchase has been successful.');
        setOrderId(null);
        getWallet(dispatch, selectedPracticeId);
        setClientSecret(null);
        setTopUpChoiceModalOpen(true);
      } else if (orderStatus === 'CANCELED' && selectedPracticeId) {
        toast.error('Your purchase has failed. Please get in touch if you believe this is an error.');
        setOrderId(null);
        getWallet(dispatch, selectedPracticeId);
        setClientSecret(null);
        setTopUpChoiceModalOpen(true);
      }
    },
    [dispatch, selectedPracticeId],
  );

  const handleSelectProductAndFetchClientSecret = async (id: string, selectedPracticeId: string) => {
    await request
      .post(`${HOST}/payments/orders?practiceId=${selectedPracticeId}`)
      .set('Content-Type', 'application/json')
      .send({
        items: [{ productId: id }],
        returnUrl: BASE_URL,
      })
      .withCredentials()
      .then(response => {
        setClientSecret(decodeURIComponent(response.body.clientSecret));
        setOrderId(response.body.orderId);
      });
    setShowPaymentModal(true);
  };

  const handleCloseTopUpChoiceModal = () => {
    setClientSecret(null);
    setOrderId(null);
    setTopUpChoiceModalOpen(false);
  };

  const handleClosePaymentCheckoutModal = async () => {
    // send backend order id to clean up data (uncompleted order)
    if (currentOrderId && !isCompleteCheckout && selectedPracticeId) {
      await deleteOrder(dispatch, currentOrderId, selectedPracticeId);
    }
    setOrderId(null);
    setClientSecret(null);
    setShowPaymentModal(false);
    if (sessionStorage?.getItem(CLOSE_TOP_UP_CHOICE_MODAL) === 'true') {
      handleCloseTopUpChoiceModal();
      dispatch({ type: SET_SHOW_DAY_PASS_FLAG, payload: true });
    }
  };

  useEffect(() => {
    setDisplaySeconds(topup?.totalUsableSeconds ?? null);
  }, [topup?.totalUsableSeconds]);

  // Real-time countdown effect when recording and less than or equal to 60 seconds
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (
      isRecordingConnectionOpen &&
      topup &&
      topup.totalUsableSeconds !== null &&
      topup.totalUsableSeconds <= 60 &&
      topup.totalUsableSeconds > 0
    ) {
      interval = setInterval(() => {
        setDisplaySeconds(prev => {
          if (prev === null || prev <= 1) {
            if (interval) clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecordingConnectionOpen, topup]);

  useEffect(() => {
    // isCompleteCheckout flag for success on payments
    if (isCompleteCheckout) {
      sessionStorage.setItem(CLOSE_TOP_UP_CHOICE_MODAL, 'true');
    } else {
      sessionStorage.setItem(CLOSE_TOP_UP_CHOICE_MODAL, 'false');
    }
    if (!isCompleteCheckout || !currentOrderId) return undefined;
    const abort = new AbortController();
    if (selectedPracticeId) {
      pollOrderStatus({
        orderId: currentOrderId,
        onUpdated: orderStatus => handleUpdatePaymentStatus(orderStatus),
        abortSignal: abort.signal,
        selectedPracticeId: selectedPracticeId,
      });
    }
    return () => {
      abort.abort();
    };
  }, [isCompleteCheckout, currentOrderId, handleUpdatePaymentStatus, selectedPracticeId]);

  if (hasUnlimitedSub(subscription)) {
    return null;
  }
  return (
    <>
      <ChipContainer backgroundColor={bgColor} data-automation="chipContainer">
        <StyledTimerIcon
          hasTimeLeft={displaySeconds !== 0 || hasActivePass}
          data-automation="StyledTimerIcon"
          data-has={String(displaySeconds !== 0)}
        />
        <TimerText hasTimeLeft={displaySeconds !== 0 || hasActivePass} data-automation="TimerText">
          {text}
          {!hasActivePass && (
            <TopUpLink
              hasTimeLeft={displaySeconds !== 0}
              onClick={handleOpenTopUpOptionsModal}
              data-automation="TopUpLink"
            >
              <strong style={{ textDecoration: 'underline', marginLeft: '4px', cursor: 'pointer' }}>
                Top up here.
              </strong>
            </TopUpLink>
          )}
        </TimerText>
      </ChipContainer>
      <MuiModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      >
        <ModalContainer
          data-automation="ModalContainerPayment"
          style={{
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            margin: 'auto',
            padding: 0,
            boxSizing: 'border-box',
            background: 'transparent',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          <ModalCloseButton
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-60px',
              color: 'white',
              width: '48px',
              height: '48px',
              zIndex: 10,
            }}
            data-automation="ModalCloseButton-payment-modal-checkout"
            type="button"
            tabIndex={0}
            onClick={handleClosePaymentCheckoutModal}
          >
            <ModalCloseIcon />
          </ModalCloseButton>
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{
              clientSecret,
              onComplete: () => setCompletedCheckout(true),
            }}
          >
            <div
              id="stripe-container"
              style={{
                width: '100%',
                maxHeight: 'calc(90vh - 16px)',
                overflow: 'auto',
                boxSizing: 'border-box',
                background: 'transparent',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              <EmbeddedCheckout />
            </div>
          </EmbeddedCheckoutProvider>
        </ModalContainer>
      </MuiModal>
      {products && (
        <TopUpModal
          isOpen={isTopUpChoiceModalOpen}
          onClose={handleCloseTopUpChoiceModal}
          products={products}
          onBuy={productId => {
            if (productId === 'DAY_PASS') {
              sessionStorage.setItem(SELECTED_PRODUCT_CHOICE, 'DAY_PASS');
            }
            if (selectedPracticeId) {
              handleSelectProductAndFetchClientSecret(productId, selectedPracticeId);
            }
          }}
        />
      )}
    </>
  );
};
